import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { ClinicContentBlock } from '../../../data/models/ClinicContentBlock';
import { ContactArea } from '../../../data/enums/ContactArea';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { CONSTANTS } from '../../../helper/constants';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-contact-person-dialog',
  imports: [ReactiveFormsModule, MatButtonModule, MatDialogModule, MatFormFieldModule, MatIconModule, MatInputModule],
  templateUrl: './contact-person-dialog.component.html',
  styleUrl: './contact-person-dialog.component.scss'
})
export class ContactPersonDialogComponent implements OnInit {
  form!: FormGroup
  isEdit: boolean
  contactAreas = Object.values(ContactArea)
  showCustomArea = false
  private isClosing = false

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<ContactPersonDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { block?: ClinicContentBlock }
  ) {
    this.isEdit = !!data?.block
  }

  ngOnInit(): void {
    this.dialogRef.backdropClick().subscribe(() => this.tryClose(null))
    this.dialogRef.keydownEvents().subscribe(e => {
      if (e.key === 'Escape') {
        e.preventDefault()
        this.tryClose(null)
      }
    })

    let initialArea = ''
    let initialCustom = ''

    if (this.isEdit && this.data.block?.contact_area) {
      initialArea = this.data.block.contact_area
      if (initialArea === ContactArea.CUSTOM) {
        initialCustom = this.data.block.contact_area_custom || ''
        this.showCustomArea = true
      }
    }

    this.form = this.fb.group({
      contact_name: [this.data?.block?.contact_name || '', [Validators.required, Validators.maxLength(CONSTANTS.maxInput)]],
      contact_function: [this.data?.block?.contact_function || '', [Validators.maxLength(CONSTANTS.maxInput)]],
      contact_area: [initialArea],
      custom_area: [initialCustom],
      contact_phone: [this.data?.block?.contact_phone || '', [Validators.maxLength(CONSTANTS.maxInput)]],
      contact_email: [this.data?.block?.contact_email || '', [Validators.maxLength(CONSTANTS.maxInput), Validators.email]],
      contact_note: [this.data?.block?.contact_note || '', [Validators.maxLength(CONSTANTS.maxInput)]]
    })
  }

  onAreaChange(value: string): void {
    this.showCustomArea = value === ContactArea.CUSTOM
    if (this.showCustomArea) {
      this.form.get('custom_area')?.setValidators([Validators.required, Validators.maxLength(CONSTANTS.maxInput)])
    } else {
      this.form.get('custom_area')?.clearValidators()
      this.form.get('custom_area')?.setValue('')
    }
    this.form.get('custom_area')?.updateValueAndValidity()
  }

  async tryClose(result: string | null): Promise<void> {
    if (this.isClosing) return
    if (this.form.dirty) {
      this.isClosing = true
      try {
        const confirmed = await this.confirmDiscard()
        if (!confirmed) return
      } finally {
        this.isClosing = false
      }
    }
    this.dialogRef.close(result)
  }

  private async confirmDiscard(): Promise<boolean> {
    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Änderungen verwerfen',
        message: 'Du hast ungespeicherte Änderungen. Wenn du den Dialog verlässt, gehen diese verloren.',
        confirmText: 'Verwerfen'
      }
    })
    return !!(await firstValueFrom(ref.afterClosed()))
  }

  save(): void {
    if (this.form.invalid) return

    const value = this.form.value
    this.dialogRef.close({
      contact_name: value.contact_name,
      contact_function: value.contact_function || null,
      contact_area: value.contact_area || null,
      contact_area_custom: this.showCustomArea ? value.custom_area : null,
      contact_phone: value.contact_phone || null,
      contact_email: value.contact_email || null,
      contact_note: value.contact_note || null
    })
  }
}
